import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAdmin } from '../../contexts/AdminContext';
import DepartureTopStrip from '../../components/ui/DepartureTopStrip';

// ---------------------------------------------------------------------------
// Styles — match SiteManagerAdmin / dashboard design system
// ---------------------------------------------------------------------------

const BTN_BASE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  fontWeight: 700,
  border: 'none',
  padding: '5px 12px',
  cursor: 'pointer',
  lineHeight: 1.4,
  transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease, opacity 120ms ease',
};

const BTN_AMBER = {
  ...BTN_BASE,
  background: 'var(--c-brand)',
  color: '#fff',
};

const BTN_DANGER = {
  ...BTN_BASE,
  background: 'transparent',
  border: '1px solid var(--c-danger)',
  color: 'var(--c-danger)',
};

const BTN_GHOST = {
  ...BTN_BASE,
  background: 'transparent',
  color: 'var(--c-ink-2)',
  border: '1px solid var(--c-line-soft)',
};

const TH_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'var(--c-ink-3)',
  padding: '10px 14px',
  textAlign: 'left',
  borderBottom: '1px solid var(--c-line-hard)',
  background: 'var(--c-bg-2)',
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '.06em',
  padding: '10px 14px',
  borderBottom: '1px solid var(--c-line-soft)',
  color: 'var(--c-ink)',
  verticalAlign: 'middle',
};

const TD_NAME_STYLE = {
  ...TD_STYLE,
  fontFamily: "'Geist', sans-serif",
  fontSize: 13,
  letterSpacing: 'normal',
  fontWeight: 500,
  color: 'var(--c-ink)',
};

const MONO_DIM = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.08em',
  color: 'var(--c-ink-3)',
  fontVariantNumeric: 'tabular-nums slashed-zero',
};

// ---------------------------------------------------------------------------
// Status badge colours
// ---------------------------------------------------------------------------

const STATUS_BADGE = {
  pending: {
    background: 'rgba(234,88,12,0.10)',
    color: 'var(--c-brand)',
    border: '1px solid rgba(234,88,12,0.25)',
  },
  paid: {
    background: 'rgba(22,163,74,0.10)',
    color: '#16A34A',
    border: '1px solid rgba(22,163,74,0.25)',
  },
  cancelled: {
    background: 'rgba(100,116,139,0.10)',
    color: 'var(--c-ink-3)',
    border: '1px solid rgba(100,116,139,0.20)',
  },
};

// ---------------------------------------------------------------------------
// Status tabs
// ---------------------------------------------------------------------------

const TABS = ['all', 'pending', 'paid', 'cancelled'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAmount(amount) {
  if (amount == null) return '—';
  return '₹' + Number(amount).toLocaleString('en-IN');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingBar() {
  return (
    <>
      <style>{`
        @keyframes igc-bar { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { .igc-loading-bar { display: none; } }
      `}</style>
      <div style={{ position: 'relative', height: 2, background: 'var(--c-line-soft)', overflow: 'hidden', marginBottom: 'var(--sp-6)' }}>
        <div
          className="igc-loading-bar"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--c-brand)',
            animation: 'igc-bar 900ms ease-in-out infinite',
          }}
        />
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.14em',
        color: 'var(--c-ink-3)',
        textTransform: 'uppercase',
        padding: '0 0 var(--sp-6)',
      }}>
        LOADING…
      </div>
    </>
  );
}

function EmptyState({ primary, secondary }) {
  return (
    <div style={{
      padding: 'var(--sp-12) var(--pad-h)',
      textAlign: 'center',
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      letterSpacing: '.14em',
      color: 'var(--c-ink-3)',
      textTransform: 'uppercase',
    }}>
      <div style={{ marginBottom: 6 }}>{primary}</div>
      {secondary && (
        <div style={{ fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>{secondary}</div>
      )}
    </div>
  );
}

function HoverRow({ children, style, ...rest }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      {...rest}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'var(--c-bg-2)' : 'transparent',
        transition: 'background 120ms ease',
        ...style,
      }}
    >
      {children}
    </tr>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.cancelled;
  return (
    <span style={{
      ...s,
      fontFamily: 'var(--font-mono)',
      fontSize: 9,
      letterSpacing: '.14em',
      textTransform: 'uppercase',
      fontWeight: 700,
      padding: '3px 8px',
      display: 'inline-block',
    }}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const IgpClaimsPage = () => {
  const { isAdmin, user } = useAdmin();

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [pending, setPending] = useState({}); // claimId → true while writing
  const [supervisorNames, setSupervisorNames] = useState({}); // uid → display_name

  // -------------------------------------------------------------------------
  // Resolve supervisor display_name (cached)
  // Hooks must be declared before any conditional return.
  // -------------------------------------------------------------------------

  const resolveSupervisorName = useCallback(async (uid) => {
    if (!uid) return;
    setSupervisorNames((prev) => {
      if (prev[uid] !== undefined) return prev; // already fetched or in-flight
      return { ...prev, [uid]: null }; // mark in-flight
    });
    try {
      const snap = await getDoc(doc(db, 'supervisors', uid));
      const name = snap.exists() ? (snap.data().display_name || null) : null;
      setSupervisorNames((prev) => ({ ...prev, [uid]: name }));
    } catch {
      setSupervisorNames((prev) => ({ ...prev, [uid]: null }));
    }
  }, []);

  // -------------------------------------------------------------------------
  // Real-time subscription to igp_claims
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isAdmin) return; // skip subscription for non-admins
    const q = query(collection(db, 'igp_claims'), orderBy('submittedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setClaims(list);
        setLoading(false);
        // Kick off supervisor name resolution for unseen UIDs
        const seen = new Set();
        list.forEach((c) => { if (c.uid && !seen.has(c.uid)) { seen.add(c.uid); } });
        seen.forEach((uid) => resolveSupervisorName(uid));
      },
      (err) => {
        console.error('IgpClaimsPage: snapshot error', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [isAdmin, resolveSupervisorName]);

  // Guard: non-admin sees access denied
  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        letterSpacing: '.14em',
        color: 'var(--c-ink-3)',
        textTransform: 'uppercase',
      }}>
        ACCESS DENIED
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const markPaid = async (claim) => {
    setPending((p) => ({ ...p, [claim.id]: 'paid' }));
    try {
      await updateDoc(doc(db, 'igp_claims', claim.id), {
        status: 'paid',
        processedAt: serverTimestamp(),
        processedBy: user?.email || '',
      });
    } catch (err) {
      console.error('IgpClaimsPage: markPaid failed', err);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[claim.id]; return n; });
    }
  };

  const cancelClaim = async (claim) => {
    setPending((p) => ({ ...p, [claim.id]: 'cancel' }));
    try {
      await updateDoc(doc(db, 'igp_claims', claim.id), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: user?.email || '',
      });
    } catch (err) {
      console.error('IgpClaimsPage: cancel failed', err);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[claim.id]; return n; });
    }
  };

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const filtered = activeTab === 'all'
    ? claims
    : claims.filter((c) => c.status === activeTab);

  const pendingCount = claims.filter((c) => c.status === 'pending').length;

  // -------------------------------------------------------------------------
  // Tab strip styles
  // -------------------------------------------------------------------------

  const tabStyle = (active) => ({
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    fontWeight: 700,
    padding: '8px 16px',
    border: 'none',
    borderBottom: active ? '2px solid var(--c-brand)' : '2px solid transparent',
    background: 'transparent',
    color: active ? 'var(--c-brand)' : 'var(--c-ink-3)',
    cursor: 'pointer',
    transition: 'color 120ms, border-color 120ms',
  });

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="db-page">
      <DepartureTopStrip />

      {/* Ribbon */}
      <div className="db-ribbon">
        <div className="db-ribbon-title">
          IGP REDEMPTION CLAIMS
          {pendingCount > 0 && (
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '.12em',
              fontWeight: 700,
              color: 'var(--c-brand)',
              marginLeft: 14,
            }}>
              {pendingCount} PENDING
            </span>
          )}
        </div>
      </div>

      {/* Tab strip */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--c-line-hard)',
        padding: '0 var(--pad-h)',
        background: 'var(--c-bg)',
      }}>
        {TABS.map((t) => (
          <button key={t} style={tabStyle(activeTab === t)} onClick={() => setActiveTab(t)}>
            {t.toUpperCase()}
            {t === 'pending' && pendingCount > 0 && (
              <span style={{
                marginLeft: 6,
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                fontWeight: 700,
                background: 'var(--c-brand)',
                color: '#fff',
                padding: '1px 5px',
                letterSpacing: '.08em',
              }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)' }}>
        {loading ? (
          <LoadingBar />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={TH_STYLE}>SUPERVISOR</th>
                  <th style={TH_STYLE}>REWARD</th>
                  <th style={{ ...TH_STYLE, textAlign: 'right' }}>POINTS</th>
                  <th style={{ ...TH_STYLE, textAlign: 'right' }}>AMOUNT</th>
                  <th style={TH_STYLE}>STATUS</th>
                  <th style={TH_STYLE}>SUBMITTED</th>
                  <th style={{ ...TH_STYLE, textAlign: 'right' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 0, borderBottom: '1px solid var(--c-line-soft)' }}>
                      <EmptyState
                        primary={activeTab === 'all' ? 'NO CLAIMS YET' : `NO ${activeTab.toUpperCase()} CLAIMS`}
                        secondary={activeTab === 'pending' ? 'All caught up' : undefined}
                      />
                    </td>
                  </tr>
                ) : (
                  filtered.map((claim) => {
                    const supName = supervisorNames[claim.uid];
                    const isBusy = !!pending[claim.id];

                    return (
                      <HoverRow key={claim.id}>
                        {/* Supervisor */}
                        <td style={TD_NAME_STYLE}>
                          <div>{supName || claim.uid}</div>
                          {supName && (
                            <div style={MONO_DIM}>{claim.uid}</div>
                          )}
                        </td>

                        {/* Reward name */}
                        <td style={TD_STYLE}>{claim.rewardName || claim.rewardId || '—'}</td>

                        {/* Points */}
                        <td style={{
                          ...TD_STYLE,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums slashed-zero',
                        }}>
                          {claim.points != null ? claim.points.toLocaleString('en-IN') : '—'}
                        </td>

                        {/* Amount */}
                        <td style={{
                          ...TD_STYLE,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums slashed-zero',
                        }}>
                          {formatAmount(claim.amount)}
                        </td>

                        {/* Status */}
                        <td style={TD_STYLE}>
                          <StatusBadge status={claim.status || 'pending'} />
                        </td>

                        {/* Submitted date */}
                        <td style={{ ...TD_STYLE, whiteSpace: 'nowrap' }}>
                          {formatDate(claim.submittedAt)}
                        </td>

                        {/* Actions */}
                        <td style={{ ...TD_STYLE, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {claim.status === 'pending' ? (
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                              <button
                                style={isBusy
                                  ? { ...BTN_AMBER, opacity: 0.6, cursor: 'not-allowed' }
                                  : BTN_AMBER}
                                disabled={isBusy}
                                onClick={() => markPaid(claim)}
                                onMouseEnter={(e) => { if (!isBusy) e.currentTarget.style.background = 'var(--c-brand-2, #C2410C)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--c-brand)'; }}
                              >
                                {pending[claim.id] === 'paid' ? '…' : 'MARK PAID'}
                              </button>
                              <button
                                style={isBusy
                                  ? { ...BTN_DANGER, opacity: 0.6, cursor: 'not-allowed' }
                                  : BTN_DANGER}
                                disabled={isBusy}
                                onClick={() => cancelClaim(claim)}
                                onMouseEnter={(e) => { if (!isBusy) e.currentTarget.style.background = 'rgba(220,38,38,0.05)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                {pending[claim.id] === 'cancel' ? '…' : 'CANCEL'}
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--c-ink-3)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>—</span>
                          )}
                        </td>
                      </HoverRow>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default IgpClaimsPage;
